import React, {useEffect, useState} from 'react';
import './DynamicModal.css';
import {FiPlus, FiX} from 'react-icons/fi';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import {DesktopTimePicker} from '@mui/x-date-pickers/DesktopTimePicker';
import {DatePicker} from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import EventOverlapService from '../../services/EventOverlapService';
import OverlapWarningModal from '../overlapWarningModal/OverlapWarningModal';

function DynamicModal({isOpen, onClose, type, initialItems = [], onSave}) {
    // Definim titlul în funcție de tip
    const modalTitle = type === 'measurements' ? 'Măsurători' : 'Montaje';
    const dateLabel = type === 'measurements' ? 'Dată măsurătoare' : 'Dată montaj';

    // Create event type based on modal type
    const eventType = type === 'measurements' ? 'MASURATORI' : 'MONTAJ';

    // Inițializăm cu elementele primite sau cu un array gol dacă nu sunt specificate
    const [dynamicItems, setDynamicItems] = useState([]);
    const [showOverlapWarning, setShowOverlapWarning] = useState(false);
    const [overlappingEvents, setOverlappingEvents] = useState([]);
    const [pendingItems, setPendingItems] = useState(null);
    const [conflictingItemIndex, setConflictingItemIndex] = useState(-1);

    // New state for time validation
    const [timeErrors, setTimeErrors] = useState({}); // Store errors per item ID

    // Function to validate time range
    const validateTimeRange = (startTime, endTime) => {
        if (!startTime || !endTime) {
            return null; // Don't validate if either time is null/undefined
        }

        const start = dayjs(`2023-01-01T${startTime}`);
        const end = dayjs(`2023-01-01T${endTime}`);

        if (start.isAfter(end) || start.isSame(end)) {
            return 'Ora de început trebuie să fie mai mică decât ora de sfârșit.';
        }

        return null; // No error
    };

    // Process initial items when component mounts or when they change
    useEffect(() => {
        if (initialItems.length > 0) {
            // Ensure all items have the required fields and format dates properly
            const processedItems = initialItems.map(item => {
                // Ensure date is in YYYY-MM-DD format
                let dateStr = item.date;
                if (dateStr && typeof dateStr === 'string' && !dateStr.includes('-')) {
                    // Convert DD.MM.YYYY to YYYY-MM-DD if needed
                    const parts = dateStr.split('.');
                    if (parts.length === 3) {
                        dateStr = `${parts[2]}-${parts[1]}-${parts[0]}`;
                    }
                }

                return {
                    id: item.id || Date.now() + Math.random(),
                    date: dateStr || new Date().toISOString().split('T')[0],
                    startTime: item.startTime || '09:00',
                    endTime: item.endTime || '10:00',
                    description: item.description || (type === 'measurements' ? 'Măsurătoare' : 'Montaj'),
                    eventType: item.eventType || eventType
                };
            });
            setDynamicItems(processedItems);
        } else {
            // Initialize with one default item
            setDynamicItems([{
                id: Date.now(),
                date: new Date().toISOString().split('T')[0],
                startTime: '09:00',
                endTime: '10:00',
                description: type === 'measurements' ? 'Măsurătoare' : 'Montaj',
                eventType: eventType
            }]);
        }

        // Clear time errors when modal opens/changes
        setTimeErrors({});
    }, [initialItems, type, eventType, isOpen]);

    const handleAddItem = () => {
        const newItem = {
            id: Date.now(), // Folosim timestamp pentru id unic
            date: new Date().toISOString().split('T')[0], // Set default date to today
            startTime: '09:00',
            endTime: '10:00',
            description: type === 'measurements' ? 'Măsurătoare' : 'Montaj',
            eventType: eventType
        };
        setDynamicItems([...dynamicItems, newItem]);
    };

    const handleRemoveItem = (id) => {
        setDynamicItems(dynamicItems.filter(item => item.id !== id));
        // Remove time error for this item
        const newTimeErrors = {...timeErrors};
        delete newTimeErrors[id];
        setTimeErrors(newTimeErrors);
    };

    const handleChange = (id, field, value) => {
        const updatedItems = dynamicItems.map(item =>
            item.id === id ? {...item, [field]: value} : item
        );
        setDynamicItems(updatedItems);

        // Handle time validation and auto-adjustment
        if (field === 'startTime' || field === 'endTime') {
            const currentItem = updatedItems.find(item => item.id === id);
            if (currentItem) {
                handleTimeValidation(id, field, value, currentItem);
            }
        }
    };

    const handleTimeValidation = (itemId, changedField, newValue, currentItem) => {
        let startTime = currentItem.startTime;
        let endTime = currentItem.endTime;
        let needsUpdate = false;

        if (changedField === 'startTime') {
            startTime = newValue;

            // Auto-adjust end time if start time >= end time
            const start = dayjs(`2023-01-01T${startTime}`);
            const end = dayjs(`2023-01-01T${endTime}`);

            if (start.isAfter(end) || start.isSame(end)) {
                const newEndTime = start.add(15, 'minute');
                endTime = newEndTime.format('HH:mm');
                needsUpdate = true;
            }
        } else if (changedField === 'endTime') {
            endTime = newValue;

            // Auto-adjust start time if end time <= start time
            const start = dayjs(`2023-01-01T${startTime}`);
            const end = dayjs(`2023-01-01T${endTime}`);

            if (end.isBefore(start) || end.isSame(start)) {
                const newStartTime = end.subtract(15, 'minute');
                startTime = newStartTime.format('HH:mm');
                needsUpdate = true;
            }
        }

        // Update the item if auto-adjustment occurred
        if (needsUpdate) {
            setDynamicItems(prevItems =>
                prevItems.map(item =>
                    item.id === itemId ? {...item, startTime, endTime} : item
                )
            );
        }

        // Validate time range and update errors
        const timeValidationError = validateTimeRange(startTime, endTime);
        setTimeErrors(prevErrors => ({
            ...prevErrors,
            [itemId]: timeValidationError
        }));
    };

    const handleSave = async () => {
        // Validate that all required fields are filled
        const areAllItemsValid = dynamicItems.every(item =>
            item.date && item.startTime && item.endTime);

        if (!areAllItemsValid) {
            alert('Te rugăm să completezi toate câmpurile obligatorii pentru fiecare element.');
            return;
        }

        // Check for time validation errors
        const hasTimeErrors = Object.values(timeErrors).some(error => error !== null && error !== undefined);
        if (hasTimeErrors) {
            alert('Te rugăm să corectezi erorile de timp înainte de salvare.');
            return;
        }

        // Check for overlaps before saving
        await checkForOverlaps();
    };

    const checkForOverlaps = async () => {
        const overlapsFound = [];

        for (let i = 0; i < dynamicItems.length; i++) {
            const item = dynamicItems[i];

            // Verifică dacă este un item nou (ID-ul nu există în initialItems)
            // sau un item modificat
            const wasInInitialItems = initialItems.some(initialItem => initialItem.id === item.id);
            const isNewItem = !wasInInitialItems;

            let isModifiedItem = false;
            if (wasInInitialItems) {
                const originalItem = initialItems.find(initialItem => initialItem.id === item.id);
                isModifiedItem = originalItem && (
                    originalItem.date !== item.date ||
                    originalItem.startTime !== item.startTime ||
                    originalItem.endTime !== item.endTime
                );
            }

            // Only check overlaps for new items or modified existing items
            if (!isNewItem && !isModifiedItem) {
                continue; // Skip this item - it's an existing unmodified item
            }

            try {
                const overlaps = await EventOverlapService.checkForOverlaps(
                    item.date,
                    item.startTime,
                    item.endTime,
                    wasInInitialItems ? item.id : null // Only exclude if it's a real server ID
                );

                if (overlaps.length > 0) {
                    overlapsFound.push({
                        itemIndex: i,
                        item: item,
                        overlaps: overlaps
                    });
                }
            } catch (error) {
                console.error('Error checking for overlaps:', error);
                // Continue with other items if one fails
            }
        }

        if (overlapsFound.length > 0) {
            // Show warning for the first conflicting item
            const firstConflict = overlapsFound[0];
            setConflictingItemIndex(firstConflict.itemIndex);
            setOverlappingEvents(firstConflict.overlaps);
            setPendingItems(dynamicItems);
            setShowOverlapWarning(true);
        } else {
            // No overlaps, proceed with saving
            saveItems();
        }
    };

    const saveItems = () => {
        // Trimitem datele către părinte dacă există un callback onSave
        if (onSave) {
            onSave(dynamicItems);
        }
        onClose();
    };

    const handleOverlapConfirm = () => {
        setShowOverlapWarning(false);
        setPendingItems(null);
        setConflictingItemIndex(-1);
        saveItems();
    };

    const handleOverlapCancel = () => {
        setShowOverlapWarning(false);
        setPendingItems(null);
        setConflictingItemIndex(-1);
    };

    // Check if there are any time validation errors
    const hasTimeErrors = Object.values(timeErrors).some(error => error !== null && error !== undefined);

    if (!isOpen) return null;

    return (
        <div className="dynamic-modal-overlay">
            <div className={`dynamic-modal-content ${type === 'measurements' ? 'measurements' : ''}`}>
                <div className="dynamic-modal-header">
                    <h2>{modalTitle}</h2>
                    <button className="dynamic-close-button" onClick={onClose}>
                        <FiX/>
                    </button>
                </div>
                <div className="dynamic-modal-body">
                    {dynamicItems.map((item) => (
                        <div key={item.id} className="dynamic-item">
                            <div className="dynamic-column">
                                <div className="dynamic-row">
                                    <div className="dynamic-label">{dateLabel}</div>
                                    <div className="dynamic-input">
                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                            <DatePicker
                                                format="DD.MM.YYYY"
                                                value={item.date ? dayjs(item.date) : null}
                                                onChange={(newDate) => {
                                                    if (newDate) {
                                                        const dateString = newDate.format('YYYY-MM-DD');
                                                        handleChange(item.id, 'date', dateString);
                                                    }
                                                }}
                                                slotProps={{
                                                    textField: {
                                                        size: "small",
                                                        fullWidth: true,
                                                        placeholder: "ZZ.LL.AAAA",
                                                        className: "dynamic-date-picker"
                                                    }
                                                }}
                                            />
                                        </LocalizationProvider>
                                    </div>
                                </div>
                                <div className="dynamic-row">
                                    <div className="dynamic-label">Ora început</div>
                                    <div className="dynamic-input">
                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                            <DesktopTimePicker
                                                format="HH:mm"
                                                ampm={false}
                                                value={dayjs(`2023-01-01T${item.startTime}`)}
                                                onChange={(newTime) => {
                                                    if (newTime) {
                                                        const timeString = newTime.format('HH:mm');
                                                        handleChange(item.id, 'startTime', timeString);
                                                    }
                                                }}
                                                slotProps={{
                                                    textField: {
                                                        size: "small",
                                                        fullWidth: true,
                                                        className: "dynamic-time-picker"
                                                    }
                                                }}
                                            />
                                        </LocalizationProvider>
                                    </div>
                                </div>
                                <div className="dynamic-row">
                                    <div className="dynamic-label">Ora sfârșit</div>
                                    <div className="dynamic-input">
                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                            <DesktopTimePicker
                                                format="HH:mm"
                                                ampm={false}
                                                value={dayjs(`2023-01-01T${item.endTime}`)}
                                                onChange={(newTime) => {
                                                    if (newTime) {
                                                        const timeString = newTime.format('HH:mm');
                                                        handleChange(item.id, 'endTime', timeString);
                                                    }
                                                }}
                                                slotProps={{
                                                    textField: {
                                                        size: "small",
                                                        fullWidth: true,
                                                        className: "dynamic-time-picker"
                                                    }
                                                }}
                                            />
                                        </LocalizationProvider>
                                    </div>
                                </div>
                                <div className="dynamic-row">
                                    <div className="dynamic-label">Descriere</div>
                                    <div className="dynamic-input">
                                        <input
                                            type="text"
                                            value={item.description || ''}
                                            onChange={(e) => handleChange(item.id, 'description', e.target.value)}
                                            placeholder="Adăugați descriere"
                                        />
                                    </div>
                                </div>

                                {/* Display time validation error for this item */}
                                {timeErrors[item.id] && (
                                    <div className="time-error-message" style={{
                                        color: '#e74c3c',
                                        fontSize: '0.8rem',
                                        padding: '0.5rem 1rem',
                                        backgroundColor: 'rgba(231, 76, 60, 0.1)',
                                        borderRadius: '4px',
                                        margin: '0.5rem 0'
                                    }}>
                                        {timeErrors[item.id]}
                                    </div>
                                )}
                            </div>
                            {dynamicItems.length > 1 && (
                                <button
                                    className="remove-dynamic-btn"
                                    onClick={() => handleRemoveItem(item.id)}
                                    aria-label="Remove item"
                                >
                                    <FiX/>
                                </button>
                            )}
                        </div>
                    ))}
                    <div className="add-dynamic-container">
                        <button
                            className="add-dynamic-btn"
                            onClick={handleAddItem}
                            aria-label="Add new item"
                        >
                            <FiPlus/>
                        </button>
                    </div>
                </div>
                <div className="dynamic-modal-footer">
                    <button
                        className="save-dynamic-btn"
                        onClick={handleSave}
                        disabled={hasTimeErrors}
                        style={{
                            opacity: hasTimeErrors ? 0.6 : 1,
                            cursor: hasTimeErrors ? 'not-allowed' : 'pointer'
                        }}
                    >
                        Salvează
                    </button>
                </div>
            </div>
            {/* Overlap Warning Modal */}
            <OverlapWarningModal
                isOpen={showOverlapWarning}
                onClose={handleOverlapCancel}
                onConfirm={handleOverlapConfirm}
                overlappingEvents={overlappingEvents}
                newEventDetails={{
                    title: conflictingItemIndex >= 0 ?
                        `${modalTitle} - ${dynamicItems[conflictingItemIndex]?.description || ''}` :
                        modalTitle,
                    startTime: conflictingItemIndex >= 0 ?
                        dynamicItems[conflictingItemIndex]?.startTime : '',
                    endTime: conflictingItemIndex >= 0 ?
                        dynamicItems[conflictingItemIndex]?.endTime : '',
                    date: conflictingItemIndex >= 0 ?
                        new Date(dynamicItems[conflictingItemIndex]?.date).toLocaleDateString('ro-RO') : ''
                }}
            />
        </div>
    );
}

export default DynamicModal;